import * as React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertGroup,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardTitle,
  CodeBlock,
  CodeBlockCode,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Dropdown,
  DropdownItem,
  DropdownList,
  EmptyState,
  EmptyStateBody,
  Grid,
  GridItem,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  SearchInput,
  Select,
  SelectOption,
  SelectList,
  SelectGroup,
  Menu,
  MenuContent,
  MenuGroup,
  MenuList,
  MenuItem,
  Divider,
  Stack,
  StackItem,
  Tabs,
  Tab,
  TabContent,
  TabTitleText,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  ToolbarToggleGroup,
  Tooltip,
} from '@patternfly/react-core';
import { StatusLabel } from '@app/components/StatusLabel';
import { EllipsisVIcon, CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, SpinnerIcon, ClockIcon, RedoIcon, DownloadIcon, InfoCircleIcon, CloudSecurityIcon, VirtualMachineIcon, ServerGroupIcon, TimesIcon, ExternalLinkAltIcon, CopyIcon, CogIcon, ExpandArrowsAltIcon, FolderIcon, QuestionCircleIcon, PauseIcon, PlayIcon, ExpandIcon, BuilderImageIcon, CaretDownIcon } from '@patternfly/react-icons';
import { Icon, Flex, FlexItem, AlertActionLink, Checkbox, Nav, NavItem, NavGroup, NavExpandable } from '@patternfly/react-core';
// Temporarily comment out LogViewer until package is installed
// import { LogViewer, LogViewerSearch } from '@patternfly/react-log-viewer';
import { getImageBuildById, ImageBuild, ImageExport, mockImageBuilds, mockImageExports } from '../data/imageBuildData';

// Temporary fallback components until @patternfly/react-log-viewer is installed
// These will be replaced once the package is installed
const LogViewer: React.FunctionComponent<{ 
  data: string[]; 
  isTextWrapped?: boolean; 
  toolbar?: React.ReactNode;
  id?: string;
  scrollToRow?: number;
  innerRef?: React.Ref<any>;
  height?: string | number;
  overScanCount?: number;
  footer?: React.ReactNode;
  onScroll?: (params: any) => void;
}> = ({ data, isTextWrapped, toolbar, id, scrollToRow, innerRef, height, overScanCount, footer, onScroll }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  React.useImperativeHandle(innerRef, () => ({
    scrollToBottom: () => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }
  }));
  
  React.useEffect(() => {
    if (scrollToRow !== undefined && containerRef.current) {
      const lineHeight = 20; // Approximate line height
      containerRef.current.scrollTop = scrollToRow * lineHeight;
    }
  }, [scrollToRow]);
  
  return (
    <div id={id} style={{ height: height || '600px', display: 'flex', flexDirection: 'column' }}>
      {toolbar}
      <div
        ref={containerRef}
        onScroll={onScroll}
        style={{
          backgroundColor: '#fafafa',
          borderTop: toolbar ? '1px solid #d2d2d2' : 'none',
          fontFamily: 'monospace',
          fontSize: '12px',
          lineHeight: '1.5',
          overflow: 'auto',
          flex: 1,
          padding: '16px',
          whiteSpace: isTextWrapped ? 'pre-wrap' : 'pre',
        }}
      >
        {data.length > 0 ? (
          data.map((line, index) => (
            <div key={index} style={{ marginBottom: '2px' }}>
              {line}
            </div>
          ))
        ) : (
          <div style={{ padding: '16px', textAlign: 'center', color: '#6a6e73' }}>
            No logs available
          </div>
        )}
      </div>
      {footer}
    </div>
  );
};

// Temporary fallback - LogViewerSearch component
// This will be replaced once @patternfly/react-log-viewer is installed
const LogViewerSearch: React.FunctionComponent<{ placeholder?: string; value?: string; onChange?: (value: string) => void; onClear?: () => void }> = ({ placeholder, value, onChange, onClear }) => {
  return (
    <SearchInput
      className="app-search-input"
      aria-label="Search logs"
      placeholder={placeholder || "Search logs..."}
      value={value || ''}
      onChange={(_, val) => onChange?.(val)}
      onClear={onClear}
    />
  );
};
import { formatDate, formatDateWithRelativeTime } from '../utils/dateFormatter';
import { getImageBuildsBasePath } from '@app/utils/appContext';

// Helper function to format export format labels
const getExportFormatLabel = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return 'VMware Disk';
    case 'qcow2':
      return 'Virtual Disk';
    case 'qcow2-container':
      return 'Disk Container';
    case 'iso':
      return 'Installer Image';
    default:
      return format.toUpperCase();
  }
};

// Helper function to get export format title without acronyms
const getExportFormatTitle = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return '.VMDK';
    case 'qcow2':
      return '.QCOW2';
    case 'qcow2-container':
      return '.QCOW2 (Container)';
    case 'iso':
      return '.ISO';
    default:
      return `.${format.toUpperCase()}`;
  }
};

// Helper function to get export format description
const getExportFormatDescription = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'iso':
      return 'For physical hardware, bare metal, and manual setups.';
    case 'qcow2-container':
      return 'For OpenShift Virtualization and cloud environments.';
    case 'vmdk':
      return 'For vSphere and enterprise VMWare platforms.';
    case 'qcow2':
      return 'For storing and pulling disks from container registries.';
    default:
      return '';
  }
};

// Helper function to get suggested export filename (base + suffix + extension per format)
const getExportFilename = (build: ImageBuild, format: string): string => {
  const base = build.outputImage ? build.outputImage.split(':')[0] : build.name;
  const formatLower = format.toLowerCase().replace(/_/g, '-');
  switch (formatLower) {
    case 'iso':
      return `${base}-installer.iso`;
    case 'qcow2-container':
      return `${base}-container-disk.qcow2`;
    case 'vmdk':
      return `${base}-vmware.vmdk`;
    case 'qcow2':
      return `${base}-generic-disk.qcow2`;
    default:
      return base;
  }
};

// Helper function to get export format file size
const getExportFormatSize = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return '1 GB';
    case 'qcow2':
      return '1.1 GB';
    case 'iso':
      return '1.5 GB';
    default:
      return '';
  }
};

// Helper function to get export format tooltip content
const getExportFormatTooltipContent = (format: string): React.ReactNode => {
  const formatLower = format.toLowerCase();
  const size = getExportFormatSize(formatLower);
  
  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
        How to use this image (~20 min total)
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Download:</strong> Save the {size} file to your local drive.
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Import:</strong> Open your VM software (e.g., VirtualBox) and select 'Import'.
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Boot:</strong> Power on the VM to complete the automated setup.
      </div>
      <div style={{ marginTop: '8px', fontStyle: 'italic', fontSize: 'var(--pf-t--global--font--size--sm)' }}>
        Note: Download speeds vary based on your network and registry access.
      </div>
    </div>
  );
};

// Helper function to format date to YYYY-MM-DD HH:mm format
const formatDateWithOrdinal = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (error) {
    return dateString;
  }
};

// Helper function to get build status badge
const getBuildStatusBadge = (status: ImageBuild['status']) => {
  const statusConfig: Record<string, { status: 'success' | 'info' | 'danger' | 'pending' | 'unknown'; label: string; pulse?: boolean }> = {
    complete: { status: 'success', label: 'Complete' },
    pushing: { status: 'info', label: 'Pushing', pulse: true },
    failed: { status: 'danger', label: 'Failed' },
    building: { status: 'info', label: 'Building', pulse: true },
    queued: { status: 'pending', label: 'Queued' },
    unknown: { status: 'unknown', label: 'Unknown' },
  };
  const config = statusConfig[status] || statusConfig.unknown;
  return (
    <StatusLabel status={config.status} pulse={config.pulse}>
      {config.label}
    </StatusLabel>
  );
};

// Helper function to get status badge
const getExportStatusBadge = (status: ImageExport['status']) => {
  const statusConfig = {
    complete: { status: 'success' as const, label: 'Complete' },
    failed: { status: 'danger' as const, label: 'Failed' },
    pending: { status: 'pending' as const, label: 'Pending' },
    exporting: { status: 'info' as const, label: 'In progress', pulse: true },
  };
  const config = statusConfig[status];
  return (
    <StatusLabel status={config.status} pulse={'pulse' in config && config.pulse}>
      {config.label}
    </StatusLabel>
  );
};

// Helper function to generate artifact reference
const getArtifactReference = (build: ImageBuild, format: string): string => {
  if (!build.registryUrl) return 'N/A';
  const formatExt = format.toLowerCase();
  // Extract base registry URL (without tag) and add format extension
  // registryUrl format: "quay.io/myorg/my-edge-img:v1.0.0"
  // Artifact format: "quay.io/myorg/my-edge-img:v1.0.0.vmdk"
  if (build.registryUrl.includes(':')) {
    return `${build.registryUrl}.${formatExt}`;
  }
  // If no tag in registryUrl, use outputImage tag
  const tag = build.outputImage && build.outputImage.includes(':') ? build.outputImage.split(':')[1] : 'latest';
  return `${build.registryUrl}:${tag}.${formatExt}`;
};

// Helper function to construct base image reference URL
const getBaseImageReferenceUrl = (build: ImageBuild): string | null => {
  if (!build.baseImageRegistry || !build.baseImageName || !build.baseImageTag) {
    return null;
  }
  // Remove protocol if present
  const registry = build.baseImageRegistry.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return `${registry}/${build.baseImageName}:${build.baseImageTag}`;
};

// Helper function to construct output image reference URL
const getOutputImageReferenceUrl = (build: ImageBuild): string | null => {
  if (!build.outputRegistry || !build.outputImageName || !build.outputTag) {
    return null;
  }
  // Remove protocol if present
  const registry = build.outputRegistry.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return `${registry}/${build.outputImageName}:${build.outputTag}`;
};

const ViewImageBuild: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const build = id ? getImageBuildById(id) : undefined;
  
  // Helper function to get the correct base path based on current location
  const getBasePath = () => getImageBuildsBasePath(location.pathname);
  
  // Check if we should navigate to a specific tab from URL params
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('tab') === 'export-images') {
      return 1; // Export images tab
    }
    if (searchParams.get('tab') === 'yaml') {
      return 2; // YAML tab
    }
    if (searchParams.get('tab') === 'logs') {
      return 3; // Logs tab
    }
    return 0; // Default to Image details tab
  };
  
  const [activeTab, setActiveTab] = React.useState<string | number>(getInitialTab());
  
  // Logs view state
  // Logs tab state - Complex Toolbar implementation
  const [selectedLogFormat, setSelectedLogFormat] = React.useState<string>('qcow2');
  const [logSearchText, setLogSearchText] = React.useState<string>('');
  const [selectedBuildName, setSelectedBuildName] = React.useState<string>(build?.name || '');
  const [isPaused, setIsPaused] = React.useState(false);
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [itemCount, setItemCount] = React.useState(1);
  const [currentItemCount, setCurrentItemCount] = React.useState(0);
  const [renderData, setRenderData] = React.useState('');
  const [selectDataSourceOpen, setSelectDataSourceOpen] = React.useState(false);
  const [timer, setTimer] = React.useState<number | null>(null);
  const [buffer, setBuffer] = React.useState<string[]>([]);
  const [linesBehind, setLinesBehind] = React.useState(0);
  const logViewerRef = React.useRef<any>(null);
  const [showDownloadToast, setShowDownloadToast] = React.useState(false);
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = React.useState(false);
  const [openExportCardActions, setOpenExportCardActions] = React.useState<Record<string, boolean>>({});
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = React.useState(false);
  
  // Update selectedBuildName when build changes
  React.useEffect(() => {
    if (build?.name) {
      setSelectedBuildName(build.name);
    }
  }, [build?.name]);

  // Generate export file names based on build
  const getExportFileName = (format: string): string => {
    if (!build) return `imagebuild-${format}-d26612`;
    const buildId = build.id.replace('build-', '');
    return `imagebuild-${buildId}-${format}-d26612`;
  };

  // Calculate the width of the longest export file name for dropdown sizing
  const getLongestExportFileName = (): string => {
    const formats = ['qcow2', 'iso', 'vmdk'];
    const names = formats.map(format => getExportFileName(format));
    return names.reduce((longest, current) => current.length > longest.length ? current : longest, '');
  };

  // Map export file names to formats
  const getDataSources = (): Record<string, { format: string; label: string }> => {
    if (!build) return {};
    return {
      [getExportFileName('qcow2')]: { format: 'qcow2', label: 'QCOW2' },
      [getExportFileName('iso')]: { format: 'iso', label: 'ISO' },
      [getExportFileName('vmdk')]: { format: 'vmdk', label: 'VMDK' }
    };
  };

  // Map selectedLogFormat to export file name (reverse mapping)
  const getExportNameFromFormat = (format: string): string => {
    const dataSources = getDataSources();
    const entry = Object.entries(dataSources).find(([_, value]) => value.format === format);
    return entry ? entry[0] : getExportFileName('qcow2');
  };

  // Initialize with Image Build option by default
  const getInitialDataSource = (): string => {
    if (build) {
      return `build-${build.id}`;
    }
    return getExportNameFromFormat(selectedLogFormat);
  };
  
  const [selectedDataSource, setSelectedDataSource] = React.useState<string>(getInitialDataSource());
  
  // YAML tab state
  const yamlViewerRef = React.useRef<any>(null);

  // Update selectedDataSource when selectedLogFormat changes
  React.useEffect(() => {
    setSelectedDataSource(getExportNameFromFormat(selectedLogFormat));
  }, [selectedLogFormat, build]);

  // Streaming effect for logs
  React.useEffect(() => {
    if (timer) {
      window.clearInterval(timer);
    }
    const newTimer = window.setInterval(() => {
      setItemCount((prev) => prev + 1);
    }, 500);
    setTimer(newTimer);
    return () => {
      window.clearInterval(newTimer);
    };
  }, [selectedDataSource]);

  // Fullscreen change listener
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen =
        document.fullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement;
      setIsFullScreen(!!isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // Removed - no longer needed with complex toolbar

  // Navigate to create build page with retry data
  const navigateToRetryBuild = () => {
    if (!build) return;
    const basePath = getBasePath();
    const createPath = `${basePath}/create`;
    navigate(createPath, { state: { retryBuild: build } });
  };

  // Navigate to create build page with duplicate data
  const navigateToDuplicateBuild = () => {
    if (!build) return;
    const basePath = getBasePath();
    const createPath = `${basePath}/create`;
    navigate(createPath, { state: { retryBuild: build } });
  };

  // Removed - no longer needed with complex toolbar
  
  // Get current log format - now based on selectedDataSource
  const getCurrentLogFormat = (): string => {
    const dataSources = getDataSources();
    return dataSources[selectedDataSource]?.format || selectedLogFormat;
  };
  
  // Update tab when URL changes
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('tab') === 'export-images') {
      setActiveTab(1);
    } else if (searchParams.get('tab') === 'yaml') {
      setActiveTab(2);
    } else if (searchParams.get('tab') === 'logs') {
      setActiveTab(3);
    }
  }, [location.search]);

  // Use mockImageExports so details page matches table (e.g. Pixel Panda has 10 exports)
  const imageExports = React.useMemo(() => mockImageExports, []);

  // Helper to get failed export for failed builds (fallback if not in imageExports)
  const getFailedExportFallback = React.useMemo(() => {
    if (!build || build.status !== 'failed') return null;
    const formats = build.exportFormats || [];
    if (formats.length === 0) return null;
    const format = formats[0];
    const formatUpper = format.toUpperCase() as 'VMDK' | 'QCOW2' | 'ISO';
    return {
      id: `export-${build.id}-${format.toLowerCase()}-failed`,
      buildId: build.id,
      buildName: build.buildName || build.name,
      format: formatUpper,
      status: 'failed' as const,
      created: build.created,
      errorMessage: 'Export failed due to insufficient storage space. Please free up space and retry.',
    };
  }, [build]);


  if (!build) {
    return (
      <PageSection>
        <Title headingLevel="h1" size="xl">Image build not found</Title>
        <Button onClick={() => navigate(getBasePath())} style={{ marginTop: 'var(--pf-t--global--spacer--md)' }}>
          Back to Image builds
        </Button>
      </PageSection>
    );
  }

  return (
    <>
      <PageSection hasBodyWrapper={false} style={{ paddingBottom: 0 }}>
        <div style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', marginBottom: 'var(--pf-t--global--spacer--md)' }}>
          <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsFlexStart' }}>
            <FlexItem>
              <Breadcrumb>
                <BreadcrumbItem to={getBasePath()} onClick={(e) => { e.preventDefault(); navigate(getBasePath()); }}>
                  Image builds
                </BreadcrumbItem>
                <BreadcrumbItem isActive>{build.buildName || build.name}</BreadcrumbItem>
              </Breadcrumb>
              <Title headingLevel="h1" size="xl" style={{ marginTop: 'var(--pf-t--global--spacer--md)' }}>
                {build.buildName || build.name}
              </Title>
            </FlexItem>
            <FlexItem>
              <Dropdown
                isOpen={isActionsDropdownOpen}
                onSelect={(event, value) => {
                  console.log('Dropdown selected value:', value);
                  setIsActionsDropdownOpen(false);
                  if (value === 'retry') {
                    navigateToRetryBuild();
                  } else if (value === 'duplicate') {
                    navigateToDuplicateBuild();
                  } else if (value === 'delete') {
                    console.log('Setting delete modal to true');
                    setShowDeleteModal(true);
                  }
                }}
                onOpenChange={(isOpen: boolean) => setIsActionsDropdownOpen(isOpen)}
                toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
                    isExpanded={isActionsDropdownOpen}
                    variant="secondary"
                    className="app-actions-toggle"
                    aria-label={`Actions for ${build.buildName || build.name}`}
                  >
                    Actions
                  </MenuToggle>
                )}
                popperProps={{
                  appendTo: () => document.body,
                  enableFlip: true,
                  position: 'end',
                  minWidth: '150px',
                  maxWidth: '200px',
                }}
              >
                <DropdownList>
                  {build.status === 'failed' ? (
                    <DropdownItem
                      key="retry"
                      value="retry"
                    >
                      Retry
                    </DropdownItem>
                  ) : (
                    <DropdownItem
                      key="duplicate"
                      value="duplicate"
                    >
                      Rebuild
                    </DropdownItem>
                  )}
                  <DropdownItem
                    key="delete"
                    value="delete"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsActionsDropdownOpen(false);
                      setShowDeleteModal(true);
                    }}
                  >
                    Delete
                  </DropdownItem>
                </DropdownList>
              </Dropdown>
            </FlexItem>
          </Flex>
        </div>
      </PageSection>

      <PageSection hasBodyWrapper={false} style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_, tabIndex) => setActiveTab(tabIndex)}
          aria-label="Image build details tabs"
        >
          <Tab eventKey={0} title={<TabTitleText>Image details</TabTitleText>}>
            <TabContent eventKey={0} id="image-details-tab" aria-label="Image details content">
              <Stack hasGutter>
                {/* Row 1: Build information | Registration */}
                <StackItem>
            <Grid hasGutter>
              <GridItem span={6}>
                <Card
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid var(--pf-t--global--color--nonstatus--gray--200)',
                    borderStyle: 'solid'
                  }}
                >
                  <CardTitle>
                    <Stack hasGutter>
                      <StackItem>
                        Build information
                      </StackItem>
                      {build.status === 'failed' && (
                        <StackItem>
                          <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                            <FlexItem>
                              <Icon status="danger">
                                <ExclamationCircleIcon />
                              </Icon>
                            </FlexItem>
                            <FlexItem>
                              <Content component="p" style={{ margin: 0, fontSize: 'var(--pf-t--global--font--size--sm)', lineHeight: 'var(--pf-t--global--line-height--body)' }}>
                                Build failed. Please retry again.
                              </Content>
                            </FlexItem>
                            <FlexItem>
                              <Button variant="link" onClick={navigateToRetryBuild} style={{ padding: 0, fontSize: 'var(--pf-t--global--font--size--sm)' }}>
                                Retry
                              </Button>
                            </FlexItem>
                          </Flex>
                        </StackItem>
                      )}
                    </Stack>
                  </CardTitle>
                  <CardBody style={{ flex: 1 }}>
                    <DescriptionList isHorizontal>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Build status</DescriptionListTerm>
                        <DescriptionListDescription>
                          {getBuildStatusBadge(build.status)}
                        </DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Name</DescriptionListTerm>
                        <DescriptionListDescription>{build.name}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Date</DescriptionListTerm>
                        <DescriptionListDescription>{formatDate(build.created)}</DescriptionListDescription>
                      </DescriptionListGroup>
                    </DescriptionList>
                  </CardBody>
                </Card>
              </GridItem>
              <GridItem span={6}>
                <Card
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid var(--pf-t--global--color--nonstatus--gray--200)',
                    borderStyle: 'solid'
                  }}
                >
                  <CardTitle>
                    Registration
                  </CardTitle>
                  <CardBody>
                    <DescriptionList isHorizontal>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Binding</DescriptionListTerm>
                        <DescriptionListDescription>{build.binding === 'early' ? 'Early binding' : 'Late binding'}</DescriptionListDescription>
                      </DescriptionListGroup>
                      {build.binding === 'early' && (
                        <>
                          <DescriptionListGroup>
                            <DescriptionListTerm>Enrollment</DescriptionListTerm>
                            <DescriptionListDescription>Auto-create certificate</DescriptionListDescription>
                          </DescriptionListGroup>
                          <DescriptionListGroup>
                            <DescriptionListTerm>Registration</DescriptionListTerm>
                            <DescriptionListDescription>1 year (Standard)</DescriptionListDescription>
                          </DescriptionListGroup>
                        </>
                      )}
                      {build.binding === 'late' && (
                        <DescriptionListGroup>
                          <DescriptionListTerm>Registration</DescriptionListTerm>
                          <DescriptionListDescription>
                            <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsFlexStart' }}>
                              <FlexItem>
                                <Icon status="info">
                                  <InfoCircleIcon />
                                </Icon>
                              </FlexItem>
                              <FlexItem>
                                Provisioning tools are enabled by default.
                              </FlexItem>
                            </Flex>
                          </DescriptionListDescription>
                        </DescriptionListGroup>
                      )}
                      <DescriptionListGroup>
                        <DescriptionListTerm>Remote access</DescriptionListTerm>
                        <DescriptionListDescription>{build.remoteAccess || '—'}</DescriptionListDescription>
                      </DescriptionListGroup>
                    </DescriptionList>
                  </CardBody>
                </Card>
              </GridItem>
            </Grid>
          </StackItem>

          {/* Row 2: Input image | Output image */}
          <StackItem>
            <Grid hasGutter>
              <GridItem span={6}>
                <Card
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid var(--pf-t--global--color--nonstatus--gray--200)',
                    borderStyle: 'solid'
                  }}
                >
                  <CardTitle>Input image</CardTitle>
                  <CardBody style={{ flex: 1 }}>
                    <DescriptionList isHorizontal>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Input image name</DescriptionListTerm>
                        <DescriptionListDescription>{build.baseImageName || 'Not set'}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Image input source repository</DescriptionListTerm>
                        <DescriptionListDescription>{build.sourceImage || 'Not set'}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Input image tag</DescriptionListTerm>
                        <DescriptionListDescription>{build.baseImageTag || 'Not set'}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Input image reference URL</DescriptionListTerm>
                        <DescriptionListDescription>
                          {(() => {
                            const url = getBaseImageReferenceUrl(build);
                            if (!url) {
                              return <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>Not set</span>;
                            }
                            return (
                              <a
                                href={`https://${url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#0066cc',
                                  textDecoration: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {url}
                                <ExternalLinkAltIcon style={{ fontSize: '0.875rem', color: '#0066cc' }} />
                              </a>
                            );
                          })()}
                        </DescriptionListDescription>
                      </DescriptionListGroup>
                    </DescriptionList>
                  </CardBody>
                </Card>
              </GridItem>
              <GridItem span={6}>
                <Card
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid var(--pf-t--global--color--nonstatus--gray--200)',
                    borderStyle: 'solid'
                  }}
                >
                  <CardTitle>Output image</CardTitle>
                  <CardBody style={{ flex: 1 }}>
                    <DescriptionList isHorizontal>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Target repository</DescriptionListTerm>
                        <DescriptionListDescription>{build.outputRegistry || 'Not set'}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Output image name</DescriptionListTerm>
                        <DescriptionListDescription>{build.outputImageName || 'Not set'}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Output image tag</DescriptionListTerm>
                        <DescriptionListDescription>{build.outputTag || 'Not set'}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Image reference URL</DescriptionListTerm>
                        <DescriptionListDescription>
                          {(() => {
                            const url = getOutputImageReferenceUrl(build);
                            if (!url) {
                              return <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>Not set</span>;
                            }
                            return (
                              <a
                                href={`https://${url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#0066cc',
                                  textDecoration: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {url}
                                <ExternalLinkAltIcon style={{ fontSize: '0.875rem', color: '#0066cc' }} />
                              </a>
                            );
                          })()}
                        </DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Export formats</DescriptionListTerm>
                        <DescriptionListDescription>
                          {build.exportFormats && build.exportFormats.length > 0
                            ? (() => {
                                const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                                const sorted = build.exportFormats ? build.exportFormats.sort((a, b) => {
                                  const aIndex = formatOrder.indexOf(a.toLowerCase());
                                  const bIndex = formatOrder.indexOf(b.toLowerCase());
                                  if (aIndex === -1) return 1;
                                  if (bIndex === -1) return -1;
                                  return aIndex - bIndex;
                                }) : [];
                                return (
                                  <Flex spaceItems={{ default: 'spaceItemsSm' }} wrap="wrap">
                                    {sorted.map((format, idx) => (
                                      <FlexItem key={idx}>
                                        <StatusLabel status="info">
                                          {getExportFormatTitle(format.toLowerCase())}
                                        </StatusLabel>
                                      </FlexItem>
                                    ))}
                                  </Flex>
                                );
                              })()
                            : 'None'}
                        </DescriptionListDescription>
                      </DescriptionListGroup>
                    </DescriptionList>
                  </CardBody>
                </Card>
              </GridItem>
            </Grid>
          </StackItem>

        </Stack>
            </TabContent>
          </Tab>
          <Tab eventKey={1} title={<TabTitleText>Export images</TabTitleText>}>
            <TabContent eventKey={1} id="export-images-tab" aria-label="Export images content">
              <Stack hasGutter>
                {(() => {
                  const buildExports = imageExports.filter(exp => exp.buildId === build.id);
                  
                  // For failed builds, show all 4 export format cards in order: ISO, QCOW2 (Container), VMDK, QCOW2
                  if (build.status === 'failed') {
                    // Define the order: ISO, QCOW2 (Container), VMDK, QCOW2
                    const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                    
                    // Create cards for all 4 formats in the specified order
                    const allFormatCards = formatOrder.map(format => {
                      const existingExport = buildExports.find(exp => {
                        const expFormatLower = exp.format.toLowerCase().replace(/_/g, '-');
                        return expFormatLower === format;
                      });
                      if (existingExport) {
                        return existingExport;
                      } else {
                        // Create placeholder export for missing format - for failed builds all show failed
                        let formatUpper: 'VMDK' | 'QCOW2' | 'ISO' | 'QCOW2-CONTAINER';
                        if (format === 'qcow2-container') {
                          formatUpper = 'QCOW2-CONTAINER';
                        } else {
                          formatUpper = format.toUpperCase() as 'VMDK' | 'QCOW2' | 'ISO';
                        }
                        const placeholderStatus: 'failed' | 'pending' = 'failed';
                        return {
                          id: `export-${build.id}-${format}-placeholder`,
                        buildId: build.id,
                        buildName: build.buildName || build.name,
                        format: formatUpper,
                          status: placeholderStatus,
                        created: build.created,
                        };
                      }
                    });
                    
                    const formatIconMap: Record<string, React.ReactElement> = {
                      'vmdk': <VirtualMachineIcon />,
                      'qcow2': <CloudSecurityIcon />,
                      'qcow2-container': <BuilderImageIcon />,
                      'iso': <ServerGroupIcon />
                    };
                    
                    return (
                      <>
                        <StackItem>
                          <Flex gap={{ default: 'gapMd' }} alignItems={{ default: 'alignItemsFlexStart' }} wrap="wrap">
                            {/* All 4 export format cards in order: ISO, QCOW2 (Container), VMDK, QCOW2 */}
                            {allFormatCards.map((exp) => {
                              const isExportFailed = exp.status === 'failed';
                              const isBuildFailed = build?.status === 'failed';
                              // For failed builds (e.g. Cosmic Noodle), all 4 cards show Failed
                              const showFailedBadge = isExportFailed || isBuildFailed;
                              return (
                                <FlexItem key={exp.id}>
                              <Card
                                style={{
                                  boxSizing: 'border-box',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  padding: '16px',
                                  position: 'relative',
                                  width: '235px',
                                  height: 'auto',
                                  minHeight: '196px',
                                  background: '#FFFFFF',
                                  border: '1px solid #C7C7C7',
                                  borderRadius: '16px',
                                }}
                              >
                                    {/* Status Badge in top right - show failed badge for QCOW2/ISO when build failed */}
                                <div 
                                  style={{
                                    position: 'absolute',
                                    right: '16px',
                                    top: '16px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 1
                                  }}
                                >
                                      {showFailedBadge ? getExportStatusBadge('failed') : getExportStatusBadge(exp.status)}
                                </div>
                                
                                {/* Card Content - Icon, Title, Description */}
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  padding: '0px',
                                  gap: '16px',
                                  width: '203px',
                                  flex: '1 1 auto',
                                  order: 0
                                }}>
                                  {/* Icon */}
                                  <div style={{
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: 'none',
                                    order: 0,
                                    flexGrow: 0
                                  }}>
                                    <Icon size="xl">
                                          {formatIconMap[exp.format.toLowerCase().replace(/_/g, '-')] || <CloudSecurityIcon />}
                                    </Icon>
                                  </div>
                                  
                                  {/* Title + Description */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    padding: '0px',
                                    gap: '4px',
                                    width: '203px',
                                    flex: 'none',
                                    order: 1,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                  }}>
                                    {/* Title + info icon (hover shows format filename) */}
                                    <div style={{
                                      width: '203px',
                                      height: '24px',
                                      fontFamily: 'var(--pf-t--global--font--family--heading)',
                                      fontStyle: 'normal',
                                          fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                          fontSize: 'var(--pf-t--global--font--size--md)',
                                          lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 'var(--pf-t--global--spacer--xs)',
                                          color: 'var(--pf-t--global--text--color--default)',
                                      flex: 'none',
                                      order: 0,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase().replace(/_/g, '-'))}</span>
                                      <Tooltip content={exp.suggestedFilename ?? getExportFilename(build, exp.format)}>
                                        <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                      </Tooltip>
                                    </div>
                                    
                                    {/* Description */}
                                    <div style={{
                                      width: '203px',
                                      fontFamily: 'var(--pf-t--global--font--family--body)',
                                      fontStyle: 'normal',
                                          fontWeight: 'var(--pf-t--global--font--weight--body)',
                                          fontSize: 'var(--pf-t--global--font--size--sm)',
                                          lineHeight: 'var(--pf-t--global--line-height--body)',
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                          color: 'var(--pf-t--global--text--color--subtle)',
                                      flex: 'none',
                                      order: 1,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                          {getExportFormatDescription(exp.format.toLowerCase().replace(/_/g, '-'))}
                                    </div>
                                  </div>
                                  
                                      {/* Buttons - different based on status */}
                                  <div style={{
                                    width: '100%',
                                    flex: 'none',
                                    order: 2,
                                    alignSelf: 'stretch',
                                    flexGrow: 0,
                                    display: 'flex',
                                    gap: 'var(--pf-t--global--spacer--sm)',
                                    alignItems: 'center'
                                  }}>
                                        {/* If build is failed, only show View logs button */}
                                        {isBuildFailed && (
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => setActiveTab(2)}
                                          >
                                            View logs
                                          </Button>
                                        )}
                                        
                                        {/* If build is not failed, show normal buttons based on export status */}
                                        {!isBuildFailed && (
                                          <>
                                            {exp.status === 'complete' && (
                                              <>
                                                <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const formatLower = exp.format.toLowerCase();
                                                    if (formatLower === 'vmdk') setShowDownloadToast(true);
                                                  }}
                                                >
                                                  Download
                                                </Button>
                                                <Dropdown
                                                  isOpen={openExportCardActions[exp.id] || false}
                                                  onOpenChange={(isOpen) => {
                                                    setOpenExportCardActions(prev => ({ ...prev, [exp.id]: isOpen }));
                                                  }}
                                                  onSelect={() => {
                                                    setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false }));
                                                  }}
                                                  toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                                    <Button
                                                      ref={toggleRef as React.Ref<HTMLButtonElement>}
                                                      variant="secondary"
                                                      size="sm"
                                                      onClick={() => {
                                                        setOpenExportCardActions(prev => ({
                                                          ...prev,
                                                          [exp.id]: !(openExportCardActions[exp.id] || false)
                                                        }));
                                                      }}
                                                      aria-expanded={openExportCardActions[exp.id] || false}
                                                      aria-label={`Actions for ${exp.format}`}
                                                      aria-haspopup="menu"
                                                    >
                                                      Action
                                                    </Button>
                                                  )}
                                                >
                                                  <DropdownList>
                                                    <DropdownItem key="view" onClick={() => setActiveTab(2)}>
                                                      View
                                                    </DropdownItem>
                                                    {build.status === 'failed' && (
                                                      <DropdownItem key="retry" onClick={() => { setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false })); navigateToRetryBuild(); }}>
                                                        Retry
                                                      </DropdownItem>
                                                    )}
                                                    {build.status === 'complete' && (
                                                      <DropdownItem key="rebuild" onClick={() => { setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false })); console.log(`Rebuild export: ${build.id}, format: ${exp.format}`); }}>
                                                        Rebuild
                                                      </DropdownItem>
                                                    )}
                                                    <DropdownItem
                                                      key="delete"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false }));
                                                        setShowDeleteModal(true);
                                                      }}
                                                    >
                                                      {['pushing', 'building', 'queued'].includes(build.status) ? 'Cancel' : 'Delete'}
                                                    </DropdownItem>
                                                  </DropdownList>
                                                </Dropdown>
                                              </>
                                            )}
                                            {exp.status === 'failed' && build.status === 'complete' && (
                                              <>
                                                <Button
                                                  variant="primary"
                                                  size="sm"
                                                  icon={<RedoIcon />}
                                                  onClick={() => console.log(`Retry export: ${exp.id}`)}
                                                >
                                                  Retry
                                                </Button>
                                                <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  onClick={() => console.log(`Delete export: ${exp.id}`)}
                                                >
                                                  Delete
                                                </Button>
                                              </>
                                            )}
                                            {exp.status === 'failed' && build.status === 'failed' && (
                                              <Button variant="secondary" size="sm" onClick={() => setActiveTab(2)}>
                                                View logs
                                              </Button>
                                            )}
                                            {(exp.status === 'pending' || exp.status === 'exporting') && (
                                              <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setActiveTab(2)}
                                              >
                                                View logs
                                              </Button>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Footer - Created date */}
                                    <div style={{
                                      width: '100%',
                                      paddingTop: 'var(--pf-t--global--spacer--md)',
                                      marginTop: 'auto',
                                      flex: 'none',
                                      order: 3,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                      <div style={{
                                        fontFamily: 'var(--pf-t--global--font--family--body)',
                                        fontStyle: 'normal',
                                        fontWeight: 'var(--pf-t--global--font--weight--body)',
                                        fontSize: 'var(--pf-t--global--font--size--sm)',
                                        lineHeight: 'var(--pf-t--global--line-height--body)',
                                        color: 'var(--pf-t--global--text--color--subtle)'
                                      }}>
                                        Date: {formatDateWithRelativeTime(exp.created)}
                                      </div>
                                    </div>
                                  </Card>
                                </FlexItem>
                              );
                            })}
                            </Flex>
                          </StackItem>
                      </>
                    );
                  }
                  
                  // For complete builds, same order as table row: last failed ISO, .QCOW2 (Container), .VMDK, .QCOW2
                  if (build.status === 'complete') {
                    const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                    const norm = (f: string) => f.toLowerCase().replace(/_/g, '-');
                    const allExports = [...buildExports]
                      .filter(exp => exp.buildId === build.id)
                      .sort((a, b) => {
                        const aIdx = formatOrder.indexOf(norm(a.format));
                        const bIdx = formatOrder.indexOf(norm(b.format));
                        if (aIdx !== bIdx) return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
                        return 0;
                      });
                    
                    if (allExports.length === 0) return null;
                    
                    const failedIsoExports = allExports
                      .filter(e => e.status === 'failed' && norm(e.format) === 'iso')
                      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
                    const newestFailedIsoExport = failedIsoExports.length > 0 ? failedIsoExports[0] : null;
                    const lastFailedIsoId = newestFailedIsoExport?.id ?? null;
                    const otherFailedIsoExports = failedIsoExports.slice(1);
                    const isoExport = allExports.find(e => norm(e.format) === 'iso');
                    const qcow2Container = allExports.find(e => norm(e.format) === 'qcow2-container');
                    const vmdk = allExports.find(e => norm(e.format) === 'vmdk');
                    const qcow2 = allExports.find(e => norm(e.format) === 'qcow2');
                    const cardsToShow = [
                      newestFailedIsoExport ?? isoExport,
                      qcow2Container,
                      vmdk,
                      qcow2,
                      ...otherFailedIsoExports
                    ].filter(Boolean) as ImageExport[];
                    
                    const formatIconMap: Record<string, React.ReactElement> = {
                      'vmdk': <VirtualMachineIcon />,
                      'qcow2': <CloudSecurityIcon />,
                      'qcow2-container': <BuilderImageIcon />,
                      'iso': <ServerGroupIcon />
                    };
                    
                    return (
                      <StackItem>
                        <Flex gap={{ default: 'gapMd' as const }} wrap="wrap">
                          {cardsToShow.map((exp) => {
                            return (
                            <FlexItem key={exp.id}>
                              <Card
                                style={{
                                  boxSizing: 'border-box',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  padding: '16px',
                                  position: 'relative',
                                  width: '235px',
                                  height: 'auto',
                                  minHeight: '196px',
                                  background: '#FFFFFF',
                                  border: '1px solid #C7C7C7',
                                  borderRadius: '16px',
                                }}
                              >
                                {/* Status Badge in top right */}
                                <div 
                                  style={{
                                    position: 'absolute',
                                    right: '16px',
                                    top: '16px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 1
                                  }}
                                >
                                  {getExportStatusBadge(exp.status)}
                                </div>
                                
                                {/* Card Content - Icon, Title, Description */}
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  padding: '0px',
                                  gap: '16px',
                                  width: '203px',
                                  flex: '1 1 auto',
                                  order: 0
                                }}>
                                  {/* Icon */}
                                  <div style={{
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: 'none',
                                    order: 0,
                                    flexGrow: 0
                                  }}>
                                    <Icon size="xl">
                                      {formatIconMap[exp.format.toLowerCase()] || <CloudSecurityIcon />}
                                    </Icon>
                                  </div>
                                  
                                  {/* Title + Description */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    padding: '0px',
                                    gap: '4px',
                                    width: '203px',
                                    flex: 'none',
                                    order: 1,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                  }}>
                                    {/* Title + info icon (hover shows format filename) */}
                                    <div style={{
                                      width: '203px',
                                      height: '24px',
                                      fontFamily: 'var(--pf-t--global--font--family--heading)',
                                      fontStyle: 'normal',
                                      fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                      fontSize: 'var(--pf-t--global--font--size--md)',
                                      lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 'var(--pf-t--global--spacer--xs)',
                                      color: 'var(--pf-t--global--text--color--default)',
                                      flex: 'none',
                                      order: 0,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase().replace(/_/g, '-'))}</span>
                                      <Tooltip content={exp.suggestedFilename ?? getExportFilename(build, exp.format)}>
                                        <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                      </Tooltip>
                                    </div>
                                    
                                    {/* Description */}
                                    <div style={{
                                      width: '203px',
                                      fontFamily: 'var(--pf-t--global--font--family--body)',
                                      fontStyle: 'normal',
                                      fontWeight: 'var(--pf-t--global--font--weight--body)',
                                      fontSize: 'var(--pf-t--global--font--size--sm)',
                                      lineHeight: 'var(--pf-t--global--line-height--body)',
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      color: 'var(--pf-t--global--text--color--subtle)',
                                      flex: 'none',
                                      order: 1,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                      {getExportFormatDescription(exp.format.toLowerCase())}
                                    </div>
                                  </div>
                                  
                                  {/* Buttons - different based on status */}
                                  <div style={{
                                    width: '100%',
                                    flex: 'none',
                                    order: 2,
                                    alignSelf: 'stretch',
                                    flexGrow: 0,
                                    display: 'flex',
                                    gap: 'var(--pf-t--global--spacer--sm)',
                                    alignItems: 'center'
                                  }}>
                                    {exp.status === 'complete' && (
                                      <>
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const formatLower = exp.format.toLowerCase();
                                            if (formatLower === 'vmdk') setShowDownloadToast(true);
                                          }}
                                        >
                                          Download
                                        </Button>
                                        <Dropdown
                                          isOpen={openExportCardActions[exp.id] || false}
                                          onOpenChange={(isOpen) => {
                                            setOpenExportCardActions(prev => ({ ...prev, [exp.id]: isOpen }));
                                          }}
                                          onSelect={() => {
                                            setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false }));
                                          }}
                                          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                            <Button
                                              ref={toggleRef as React.Ref<HTMLButtonElement>}
                                              variant="secondary"
                                              size="sm"
                                              icon={<CaretDownIcon />}
                                              iconPosition="right"
                                              onClick={() => {
                                                setOpenExportCardActions(prev => ({
                                                  ...prev,
                                                  [exp.id]: !(openExportCardActions[exp.id] || false)
                                                }));
                                              }}
                                              aria-expanded={openExportCardActions[exp.id] || false}
                                              aria-label={`Actions for ${exp.format}`}
                                              aria-haspopup="menu"
                                            >
                                              Actions
                                            </Button>
                                          )}
                                        >
<DropdownList>
                                            <DropdownItem key="view" onClick={() => setActiveTab(2)}>View</DropdownItem>
                                            {build.status === 'failed' && (
                                              <DropdownItem key="retry" onClick={() => { setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false })); navigateToRetryBuild(); }}>
                                                Retry
                                              </DropdownItem>
                                            )}
                                            {build.status === 'complete' && (
                                              <DropdownItem key="rebuild" onClick={() => { setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false })); console.log(`Rebuild export: ${build.id}, format: ${exp.format}`); }}>
                                                Rebuild
                                              </DropdownItem>
                                            )}
                                            <DropdownItem
                                              key="delete"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false }));
                                                setShowDeleteModal(true);
                                              }}
                                            >
                                              {['pushing', 'building', 'queued'].includes(build.status) ? 'Cancel' : 'Delete'}
                                            </DropdownItem>
                                          </DropdownList>
                                        </Dropdown>
                                      </>
                                    )}
                                    {exp.status === 'failed' && build.status === 'complete' && (
                                      <>
                                        {exp.id === lastFailedIsoId && (
                                          <Button
                                            variant="primary"
                                            size="sm"
                                            icon={<RedoIcon />}
                                            onClick={() => console.log(`Retry export: ${exp.id}`)}
                                          >
                                            Retry
                                          </Button>
                                        )}
                                        <Dropdown
                                          isOpen={openExportCardActions[exp.id] || false}
                                          onOpenChange={(isOpen) => {
                                            setOpenExportCardActions(prev => ({ ...prev, [exp.id]: isOpen }));
                                          }}
                                          onSelect={() => {
                                            setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false }));
                                          }}
                                          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                            <Button
                                              ref={toggleRef as React.Ref<HTMLButtonElement>}
                                              variant="secondary"
                                              size="sm"
                                              icon={<CaretDownIcon />}
                                              iconPosition="right"
                                              onClick={() => {
                                                setOpenExportCardActions(prev => ({
                                                  ...prev,
                                                  [exp.id]: !(openExportCardActions[exp.id] || false)
                                                }));
                                              }}
                                              aria-expanded={openExportCardActions[exp.id] || false}
                                              aria-label={`Actions for ${exp.format}`}
                                              aria-haspopup="menu"
                                            >
                                              Actions
                                            </Button>
                                          )}
                                        >
                                          <DropdownList>
                                            <DropdownItem key="view-logs" onClick={() => { setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false })); setActiveTab(2); }}>
                                              View logs
                                            </DropdownItem>
                                            <DropdownItem
                                              key="delete"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setOpenExportCardActions(prev => ({ ...prev, [exp.id]: false }));
                                                setShowDeleteModal(true);
                                              }}
                                            >
                                              Delete
                                            </DropdownItem>
                                          </DropdownList>
                                        </Dropdown>
                                      </>
                                    )}
                                    {exp.status === 'failed' && build.status === 'failed' && (
                                      <Button variant="secondary" size="sm" onClick={() => setActiveTab(2)}>
                                        View logs
                                      </Button>
                                    )}
                                    {(exp.status === 'pending' || exp.status === 'exporting') && (
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setActiveTab(2)}
                                      >
                                        View logs
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Footer - Created date */}
                                <div style={{
                                  width: '100%',
                                  paddingTop: 'var(--pf-t--global--spacer--md)',
                                  marginTop: 'auto',
                                  flex: 'none',
                                  order: 3,
                                  alignSelf: 'stretch',
                                  flexGrow: 0
                                }}>
                                  <div style={{
                                    fontFamily: 'var(--pf-t--global--font--family--body)',
                                    fontStyle: 'normal',
                                    fontWeight: 'var(--pf-t--global--font--weight--body)',
                                    fontSize: 'var(--pf-t--global--font--size--sm)',
                                    lineHeight: 'var(--pf-t--global--line-height--body)',
                                    color: 'var(--pf-t--global--text--color--subtle)'
                                  }}>
                                    Date: {formatDateWithRelativeTime(exp.created)}
                                  </div>
                                </div>
                              </Card>
                            </FlexItem>
                            );
                          })}
                        </Flex>
                      </StackItem>
                    );
                  }
                  
                  // For queued builds, show pending cards
                  if (build.status === 'queued') {
                    const pendingExports = buildExports.filter(exp => exp.status === 'pending' || exp.status === 'exporting');
                    if (pendingExports.length === 0) return null;
                    
                    const formatIconMap: Record<string, React.ReactElement> = {
                      'vmdk': <VirtualMachineIcon />,
                      'qcow2': <CloudSecurityIcon />,
                      'qcow2-container': <BuilderImageIcon />,
                      'iso': <ServerGroupIcon />
                    };
                    
                    return (
                      <StackItem>
                        <Flex gap={{ default: 'gapMd' as const }} wrap="wrap">
                          {pendingExports.map((exp) => {
                            const isMetalInstaller = exp.format.toLowerCase() === 'iso';
                            const isQueued = build.status === 'queued';
                            
                            return (
                              <FlexItem key={exp.id}>
                                <Card
                                  style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    padding: '16px',
                                    position: 'relative',
                                    width: '235px',
                                    height: 'auto',
                                    minHeight: '196px',
                                    background: '#FFFFFF',
                                    border: '1px solid #C7C7C7',
                                    borderRadius: '16px',
                                  }}
                                >
                                  {/* Status Badge in top right */}
                                    <div 
                                      style={{
                                        position: 'absolute',
                                        right: '16px',
                                        top: '16px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        zIndex: 1
                                      }}
                                    >
                                    {getExportStatusBadge(exp.status)}
                                    </div>
                                  
                                  {/* Card Content - Icon, Title, Description */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    padding: '0px',
                                    gap: '16px',
                                    width: '203px',
                                    flex: '1 1 auto',
                                    order: 0
                                  }}>
                                    {/* Icon */}
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flex: 'none',
                                      order: 0,
                                      flexGrow: 0
                                    }}>
                                      <Icon size="xl">
                                        {formatIconMap[exp.format.toLowerCase()] || <CloudSecurityIcon />}
                                      </Icon>
                                    </div>
                                    
                                    {/* Title + Description */}
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-start',
                                      padding: '0px',
                                      gap: '4px',
                                      width: '203px',
                                      flex: 'none',
                                      order: 1,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                      {/* Title + info icon (hover shows format filename) */}
                                      <div style={{
                                        width: '203px',
                                        height: '24px',
                                        fontFamily: 'var(--pf-t--global--font--family--heading)',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--pf-t--global--spacer--xs)',
                                        color: '#151515',
                                        flex: 'none',
                                        order: 0,
                                        alignSelf: 'stretch',
                                        flexGrow: 0
                                      }}>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase().replace(/_/g, '-'))}</span>
                                        <Tooltip content={exp.suggestedFilename ?? getExportFilename(build, exp.format)}>
                                          <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                        </Tooltip>
                                      </div>
                                      
                                      {/* Description */}
                                      <div style={{
                                        width: '203px',
                                        fontFamily: 'var(--pf-t--global--font--family--body)',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: '12px',
                                        lineHeight: '18px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        color: '#4D4D4D',
                                        flex: 'none',
                                        order: 1,
                                        alignSelf: 'stretch',
                                        flexGrow: 0
                                      }}>
                                        {getExportFormatDescription(exp.format.toLowerCase())}
                                      </div>
                                    </div>
                                    
                                    {/* View logs button - For Metal installer in queued builds, show "Export image" */}
                                    <div style={{
                                      width: '100%',
                                      flex: 'none',
                                      order: 2,
                                      alignSelf: 'stretch',
                                      flexGrow: 0,
                                      display: 'flex',
                                      gap: 'var(--pf-t--global--spacer--sm)',
                                      alignItems: 'center'
                                    }}>
                                      <Button
                                        variant="secondary"
                                        onClick={() => {
                                          if (isMetalInstaller && isQueued) {
                                            // Navigate to Export image flow
                                            navigate(`${getBasePath()}/create-export`);
                                          } else {
                                            setActiveTab(2);
                                          }
                                        }}
                                      >
                                        {isMetalInstaller && isQueued ? 'Export image' : 'View logs'}
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Footer - Created date */}
                                  <div style={{
                                    width: '100%',
                                    paddingTop: 'var(--pf-t--global--spacer--md)',
                                    marginTop: 'auto',
                                    flex: 'none',
                                    order: 3,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                  }}>
                                    <div style={{
                                      fontFamily: 'var(--pf-t--global--font--family--body)',
                                      fontStyle: 'normal',
                                        fontWeight: 'var(--pf-t--global--font--weight--body)',
                                        fontSize: 'var(--pf-t--global--font--size--sm)',
                                        lineHeight: 'var(--pf-t--global--line-height--body)',
                                        color: 'var(--pf-t--global--text--color--subtle)'
                                    }}>
                                      Date: {formatDateWithRelativeTime(exp.created)}
                                    </div>
                                  </div>
                                </Card>
                              </FlexItem>
                            );
                          })}
                        </Flex>
                      </StackItem>
                    );
                  }
                  
                  // For building and pushing builds, show 4 format cards with status badges and View logs (same as table expanded row)
                  if (build.status === 'building' || build.status === 'pushing') {
                    // Get all exports for this build
                    let allExports = buildExports.filter(exp => exp.buildId === build.id);
                    
                    // Ensure we have exactly 4 cards with all four formats (iso, qcow2-container, vmdk, qcow2)
                    const requiredFormats = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                    const existingFormats = allExports.map(exp => exp.format.toLowerCase());
                    
                    // Create missing export formats if needed
                    const normFormat = (f: string) => f.toLowerCase().replace(/_/g, '-');
                    requiredFormats.forEach((format, index) => {
                      if (!existingFormats.includes(format) && !existingFormats.includes(normFormat(format))) {
                        let formatUpper: 'VMDK' | 'QCOW2' | 'ISO' | 'QCOW2-CONTAINER';
                        if (format === 'qcow2-container') formatUpper = 'QCOW2-CONTAINER';
                        else formatUpper = format.toUpperCase() as 'VMDK' | 'QCOW2' | 'ISO';
                        allExports.push({
                          id: `export-${build.id}-${format}-${index}`,
                          buildId: build.id,
                          buildName: build.buildName || build.name,
                          format: formatUpper,
                          status: 'pending' as const,
                          created: build.created,
                        });
                      }
                    });
                    
                    // Sort to ensure consistent order: iso, qcow2-container, vmdk, qcow2
                    const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                    allExports.sort((a, b) => {
                        const aFormatLower = a.format.toLowerCase().replace(/_/g, '-');
                        const bFormatLower = b.format.toLowerCase().replace(/_/g, '-');
                        const aIndex = formatOrder.indexOf(aFormatLower);
                        const bIndex = formatOrder.indexOf(bFormatLower);
                      return aIndex - bIndex;
                    });
                    
                    // Take exactly 4 exports
                    const exportsToShow = allExports.slice(0, 4);
                    
                    const formatIconMap: Record<string, React.ReactElement> = {
                      'vmdk': <VirtualMachineIcon />,
                      'qcow2': <CloudSecurityIcon />,
                      'qcow2-container': <BuilderImageIcon />,
                      'iso': <ServerGroupIcon />
                    };
                    
                    return (
                      <StackItem>
                        <Flex gap={{ default: 'gapMd' as const }} wrap="wrap">
                          {exportsToShow.map((exp) => (
                            <FlexItem key={exp.id}>
                              <Card
                                style={{
                                  boxSizing: 'border-box',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  padding: '16px',
                                  position: 'relative',
                                  width: '235px',
                                  height: 'auto',
                                  minHeight: '196px',
                                  background: '#FFFFFF',
                                  border: '1px solid #C7C7C7',
                                  borderRadius: '16px',
                                }}
                              >
                                {/* Status Badge - always Pending for building/pushing (same as table expanded row) */}
                                <div 
                                  style={{
                                    position: 'absolute',
                                    right: '16px',
                                    top: '16px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 1
                                  }}
                                >
                                  {getExportStatusBadge('pending')}
                                </div>
                                
                                {/* Card Content - Icon, Title, Description */}
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  padding: '0px',
                                  gap: '16px',
                                  width: '203px',
                                  flex: '1 1 auto',
                                  order: 0
                                }}>
                                  {/* Icon */}
                                  <div style={{
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: 'none',
                                    order: 0,
                                    flexGrow: 0
                                  }}>
                                    <Icon size="xl">
                                      {formatIconMap[exp.format.toLowerCase()] || <CloudSecurityIcon />}
                                    </Icon>
                                  </div>
                                  
                                  {/* Title + Description */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    padding: '0px',
                                    gap: '4px',
                                    width: '203px',
                                    flex: 'none',
                                    order: 1,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                  }}>
                                    {/* Title + info icon (hover shows format filename) */}
                                    <div style={{
                                      width: '203px',
                                      height: '24px',
                                      fontFamily: 'var(--pf-t--global--font--family--heading)',
                                      fontStyle: 'normal',
                                      fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                      fontSize: 'var(--pf-t--global--font--size--md)',
                                      lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 'var(--pf-t--global--spacer--xs)',
                                      color: 'var(--pf-t--global--text--color--default)',
                                      flex: 'none',
                                      order: 0,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getExportFormatTitle(exp.format.toLowerCase().replace(/_/g, '-'))}</span>
                                      <Tooltip content={exp.suggestedFilename ?? getExportFilename(build, exp.format)}>
                                        <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon size="sm"><InfoCircleIcon /></Icon></span>
                                      </Tooltip>
                                    </div>
                                    
                                    {/* Description */}
                                    <div style={{
                                      width: '203px',
                                      fontFamily: 'var(--pf-t--global--font--family--body)',
                                      fontStyle: 'normal',
                                      fontWeight: 'var(--pf-t--global--font--weight--body)',
                                      fontSize: 'var(--pf-t--global--font--size--sm)',
                                      lineHeight: 'var(--pf-t--global--line-height--body)',
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      color: 'var(--pf-t--global--text--color--subtle)',
                                      flex: 'none',
                                      order: 1,
                                      alignSelf: 'stretch',
                                      flexGrow: 0
                                    }}>
                                      {getExportFormatDescription(exp.format.toLowerCase())}
                                    </div>
                                  </div>
                                  
                                  {/* View logs button - same as table expanded row for building/pushing */}
                                  <div style={{
                                    width: '100%',
                                    flex: 'none',
                                    order: 2,
                                    alignSelf: 'stretch',
                                    flexGrow: 0,
                                    display: 'flex',
                                    gap: 'var(--pf-t--global--spacer--sm)',
                                    alignItems: 'center'
                                  }}>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => setActiveTab(3)}
                                    >
                                      View logs
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Footer - Created date */}
                                <div style={{
                                  width: '100%',
                                  paddingTop: 'var(--pf-t--global--spacer--md)',
                                  marginTop: 'auto',
                                  flex: 'none',
                                  order: 3,
                                  alignSelf: 'stretch',
                                  flexGrow: 0
                                }}>
                                  <div style={{
                                    fontFamily: 'var(--pf-t--global--font--family--body)',
                                    fontStyle: 'normal',
                                        fontWeight: 'var(--pf-t--global--font--weight--body)',
                                        fontSize: 'var(--pf-t--global--font--size--sm)',
                                        lineHeight: 'var(--pf-t--global--line-height--body)',
                                        color: 'var(--pf-t--global--text--color--subtle)'
                                  }}>
                                      Date: {formatDateWithRelativeTime(exp.created)}
                                  </div>
                                </div>
                              </Card>
                            </FlexItem>
                          ))}
                        </Flex>
                      </StackItem>
                    );
                  }
                  
                  return null;
                })()}
              </Stack>
            </TabContent>
          </Tab>
          <Tab eventKey={2} title={<TabTitleText>YAML</TabTitleText>}>
            <TabContent eventKey={2} id="yaml-tab" aria-label="YAML content">
              <PageSection style={{ paddingTop: 0, paddingBottom: 0, marginTop: 0 }}>
                {build ? (() => {
                  // Get YAML content - single YAML without export format selection
                  const buildId = build.id.replace('build-', '');
                  const exportName = `imagebuild-${buildId}-qcow2-d26612`;
                  
                  const yamlContent = `apiVersion: v1beta1
kind: ImageExport
metadata:
  annotations: {}
  creationTimestamp: "2026-01-20T14:14:41.823603Z"
  generation: 1
  labels: {}
  name: ${exportName}
  resourceVersion: "1"
spec:
  destination:
    imageName: rh_ee_camadorg/test-image-build-api
    repository: write-oci-registry
    tag: demo-v1
  format: qcow2
  source:
    imageBuildRef: imagebuild-${buildId}
    type: imageBuild
status:
  conditions:
  - lastTransitionTime: "2026-01-20T14:20:27.499207479Z"
    message: Export completed successfully
    reason: Completed
    status: "True"
    type: Ready
  lastSeen: "2026-01-20T14:20:11.767856752Z"
  manifestDigest: sha256:ae0d9361b51efe23a64d191c5677033e34e13aba5de084977999ba856fe21c2a`;

                  const yamlLines = yamlContent.split('\n');


                  const maxLineNumberWidth = String(yamlLines.length).length;

                  return (
                    <div id="yaml-viewer-demo" style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
                      <div
                        ref={yamlViewerRef}
                        style={{
                          backgroundColor: '#fafafa',
                          border: '1px solid #d2d2d2',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          lineHeight: '1.5',
                          overflow: 'auto',
                          flex: 1,
                          display: 'flex',
                          whiteSpace: 'pre',
                        }}
                      >
                        {/* Line Numbers */}
                        <div
                          style={{
                            backgroundColor: '#fafafa',
                            borderRight: '1px solid #d2d2d2',
                            color: '#6a6e73',
                            padding: '16px 8px 16px 12px',
                            textAlign: 'right',
                            userSelect: 'none',
                            minWidth: `${(maxLineNumberWidth + 1) * 8}px`,
                            fontFamily: 'monospace',
                            fontSize: '12px',
                          }}
                        >
                          {yamlLines.map((_, index) => (
                            <div key={index} style={{ lineHeight: '1.5', paddingRight: '8px' }}>
                              {index + 1}
                            </div>
                          ))}
                        </div>

                        {/* YAML Code */}
                        <div
                          style={{
                            flex: 1,
                            padding: '16px',
                            overflowX: 'auto',
                            overflowY: 'auto',
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <CodeBlock>
                            <CodeBlockCode style={{ 
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              lineHeight: '1.5',
                              whiteSpace: 'pre',
                              margin: 0,
                              padding: 0,
                            }}>
                              {yamlContent}
                            </CodeBlockCode>
                          </CodeBlock>
                        </div>
                      </div>
                    </div>
                  );
                })() : (
                  <EmptyState>
                    <EmptyStateBody>
                      Build not found
                    </EmptyStateBody>
                  </EmptyState>
                )}
              </PageSection>
            </TabContent>
          </Tab>
          <Tab eventKey={3} title={<TabTitleText>Logs</TabTitleText>}>
            <TabContent eventKey={3} id="logs-tab" aria-label="Logs content">
              <PageSection style={{ paddingTop: 0, paddingBottom: 0, marginTop: 0 }}>
                {build ? (() => {
                  // Get unique build names for the filter dropdown
                  const uniqueBuildNames = Array.from(new Set(mockImageBuilds.map(b => b.name))).sort();
                  
                  // Get the selected build by name (or use current build if no selection)
                  const selectedBuild = selectedBuildName 
                    ? mockImageBuilds.find(b => b.name === selectedBuildName) || build
                    : build;
                  
                  const buildExports = imageExports.filter(exp => exp.buildId === selectedBuild.id);
                  
                  // Get exports for the three formats
                  const qcow2Export = buildExports.find(exp => exp.format.toLowerCase() === 'qcow2');
                  const isoExport = buildExports.find(exp => exp.format.toLowerCase() === 'iso');
                  const vmdkExport = buildExports.find(exp => exp.format.toLowerCase() === 'vmdk');
                  
                  // Log line interface with metadata
                  type LogLine = {
                    id: string;
                    text: string;
                    phase: 'BUILD' | 'EXPORT' | 'SYSTEM';
                    level: 'INFO' | 'ERROR' | 'WARN' | 'SYSTEM';
                    lineNumber: number;
                    isError: boolean;
                    isSuccess: boolean;
                  };
                  
                  // Generate logs with [BUILD] and [EXPORT] tags
                  const generateLogs = (format: string, exportTask: ImageExport | undefined, buildStatus: ImageBuild['status']): LogLine[] => {
                    const buildName = selectedBuild.buildName || selectedBuild.name;
                    const logs: LogLine[] = [];
                    let lineNumber = 1;
                    
                    const addLog = (text: string, phase: 'BUILD' | 'EXPORT' | 'SYSTEM', level: 'INFO' | 'ERROR' | 'WARN' | 'SYSTEM' = 'INFO') => {
                      const isError = level === 'ERROR' || text.toLowerCase().includes('failed') || text.toLowerCase().includes('error');
                      const isSuccess = level === 'INFO' && (text.toLowerCase().includes('successful') || text.toLowerCase().includes('complete'));
                      logs.push({
                        id: `log-${lineNumber}`,
                        text: `[${phase}] ${text}`,
                        phase,
                        level,
                        lineNumber: lineNumber++,
                        isError,
                        isSuccess
                      });
                    };
                    
                    // Base logs
                    addLog(`[2026-01-03 11:00:01] INFO: Initializing Image Build: ${buildName}`, 'BUILD', 'INFO');
                    addLog('[2026-01-03 11:00:05] INFO: Selected Formats: Virtualization, Openstack/KVM, Metal installer', 'BUILD', 'INFO');
                    
                    // If build failed, generate failed logs
                    if (buildStatus === 'failed') {
                      addLog('[2026-01-03 11:00:10] SYSTEM: Resource lock acquired by [VIRTUALIZATION]', 'SYSTEM', 'SYSTEM');
                      addLog('[2026-01-03 11:00:10] SYSTEM: [OPENSTACK/KVM] entered \'PENDING\' state (Waiting for lock...)', 'SYSTEM', 'SYSTEM');
                      addLog('[2026-01-03 11:00:10] SYSTEM: [METAL-INSTALLER] entered \'PENDING\' state (Waiting for lock...)', 'SYSTEM', 'SYSTEM');
                      addLog('', 'SYSTEM', 'SYSTEM');
                      addLog('[2026-01-03 11:01:45] Running osbuild-worker...', 'BUILD', 'INFO');
                      addLog('[2026-01-03 11:05:20] Processing blueprint...', 'BUILD', 'INFO');
                      addLog('[2026-01-03 11:08:12] ERROR: Export failed - insufficient disk space', 'EXPORT', 'ERROR');
                      addLog('[2026-01-03 11:08:13] ERROR: Build process terminated due to critical error', 'BUILD', 'ERROR');
                      addLog('[2026-01-03 11:08:14] SYSTEM: Resource lock released by [VIRTUALIZATION]', 'SYSTEM', 'SYSTEM');
                      
                      if (format === 'qcow2') {
                        addLog('', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:15] SYSTEM: Resource lock acquired by [OPENSTACK/KVM]', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:16] ERROR: Generating VMware image failed - insufficient disk space', 'EXPORT', 'ERROR');
                        addLog('[2026-01-03 11:08:17] ERROR: Build process terminated due to critical error', 'BUILD', 'ERROR');
                      } else if (format === 'iso') {
                        addLog('', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:15] SYSTEM: Resource lock acquired by [OPENSTACK/KVM]', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:16] ERROR: Generating VMware image failed - insufficient disk space', 'EXPORT', 'ERROR');
                        addLog('[2026-01-03 11:08:17] ERROR: Build process terminated due to critical error', 'BUILD', 'ERROR');
                        addLog('', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:12:05] ERROR: Writing image failed - insufficient disk space', 'EXPORT', 'ERROR');
                      } else if (format === 'vmdk') {
                        addLog('[2026-01-03 11:08:12] ERROR: Generating VMware vSphere compatible image failed', 'EXPORT', 'ERROR');
                      }
                    } else {
                      // Success logs
                      addLog('[2026-01-03 11:00:10] SYSTEM: Resource lock acquired by [VIRTUALIZATION]', 'SYSTEM', 'SYSTEM');
                      addLog('[2026-01-03 11:00:10] SYSTEM: [OPENSTACK/KVM] entered \'PENDING\' state (Waiting for lock...)', 'SYSTEM', 'SYSTEM');
                      addLog('[2026-01-03 11:00:10] SYSTEM: [METAL-INSTALLER] entered \'PENDING\' state (Waiting for lock...)', 'SYSTEM', 'SYSTEM');
                      addLog('', 'SYSTEM', 'SYSTEM');
                      addLog('[2026-01-03 11:01:45] Running osbuild-worker...', 'BUILD', 'INFO');
                      addLog('[2026-01-03 11:05:20] Processing blueprint...', 'BUILD', 'INFO');
                      
                      if (format === 'vmdk') {
                        addLog('[2026-01-03 11:05:20] Generating VMware vSphere compatible image...', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:08:12] Export successful.', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:08:13] SYSTEM: Resource lock released by [VIRTUALIZATION]', 'SYSTEM', 'SYSTEM');
                      } else if (format === 'qcow2') {
                        addLog('[2026-01-03 11:05:20] Generating VMware vSphere compatible image...', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:08:12] Export successful.', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:08:13] SYSTEM: Resource lock released by [VIRTUALIZATION]', 'SYSTEM', 'SYSTEM');
                        addLog('', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:14] SYSTEM: Resource lock acquired by [OPENSTACK/KVM]', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:15] Starting KVM-based cloud image generation...', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:09:40] Injecting cloud-init configurations...', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:12:00] Image build in progress (45%)...', 'EXPORT', 'INFO');
                      } else if (format === 'iso') {
                        addLog('[2026-01-03 11:05:20] Generating VMware vSphere compatible image...', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:08:12] Export successful.', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:08:13] SYSTEM: Resource lock released by [VIRTUALIZATION]', 'SYSTEM', 'SYSTEM');
                        addLog('', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:14] SYSTEM: Resource lock acquired by [OPENSTACK/KVM]', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:08:15] Starting KVM-based cloud image generation...', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:09:40] Injecting cloud-init configurations...', 'EXPORT', 'INFO');
                        addLog('[2026-01-03 11:12:00] Image build in progress (45%)...', 'EXPORT', 'INFO');
                        addLog('', 'SYSTEM', 'SYSTEM');
                        addLog('[2026-01-03 11:12:05] SYSTEM: [METAL-INSTALLER] is still WAITING for resource lock.', 'SYSTEM', 'SYSTEM');
                      }
                    }
                    
                    return logs;
                  };
                  
                  // Safety check: ensure selectedBuild exists
                  if (!selectedBuild) {
                    return <EmptyState><EmptyStateBody>No build selected</EmptyStateBody></EmptyState>;
                  }
                  
                  const qcow2Logs = generateLogs('qcow2', qcow2Export, selectedBuild.status);
                  const isoLogs = generateLogs('iso', isoExport, selectedBuild.status);
                  const vmdkLogs = generateLogs('vmdk', vmdkExport, selectedBuild.status);
                  
                  // Define buildOptionValue early for use throughout the logs tab section
                  const buildOptionValue = `build-${selectedBuild.id}`;
                  
                  // Create data sources based on selectedBuild early for use throughout
                  const getDataSourcesForSelectedBuild = (): Record<string, { format: string; label: string }> => {
                    if (!selectedBuild) return {};
                    const buildId = selectedBuild.id.replace('build-', '');
                    const getExportFileNameForSelected = (format: string): string => {
                      return `imagebuild-${buildId}-${format}-d26612`;
                    };
                    return {
                      [getExportFileNameForSelected('qcow2')]: { format: 'qcow2', label: 'QCOW2' },
                      [getExportFileNameForSelected('iso')]: { format: 'iso', label: 'ISO' },
                      [getExportFileNameForSelected('vmdk')]: { format: 'vmdk', label: 'VMDK' }
                    };
                  };
                  
                  const exportDataSourcesForMenu = getDataSourcesForSelectedBuild();
                  
                  // Get current logs based on selected format - using selectedDataSource
                  // Note: This function is not used anymore, but keeping for reference
                  const getCurrentLogs = (): LogLine[] => {
                    const exportDataSourcesForLogs = exportDataSourcesForMenu;
                    const format = exportDataSourcesForLogs[selectedDataSource]?.format || 'qcow2';
                    switch (format) {
                      case 'qcow2':
                        return qcow2Logs;
                      case 'iso':
                        return isoLogs;
                      case 'vmdk':
                        return vmdkLogs;
                      default:
                        return [];
                    }
                  };
                  
                  // Parse attribute-value search query (e.g., "stage:export status:error")
                  const parseSearchQuery = (query: string): { attributes: Record<string, string>, text: string } => {
                    const attributes: Record<string, string> = {};
                    let text = query;
                    
                    // Match patterns like "key:value"
                    const attrPattern = /(\w+):(\S+)/g;
                    let match;
                    while ((match = attrPattern.exec(query)) !== null) {
                      attributes[match[1].toLowerCase()] = match[2].toLowerCase();
                      text = text.replace(match[0], '').trim();
                    }
                    
                    return { attributes, text };
                  };
                  
                  // Get logs based on selectedDataSource (Image Build or Export)
                  // Get logs for the selected source
                  const getCurrentLogsForSource = (): LogLine[] => {
                    // If Image Build is selected, return all logs (BUILD and EXPORT phases)
                    if (selectedDataSource === buildOptionValue) {
                      // Combine all export logs to get full log set
                      return [...qcow2Logs, ...isoLogs, ...vmdkLogs];
                    }
                    
                    // Otherwise, get export logs for the selected format
                    const currentDataSources = exportDataSourcesForMenu;
                    const currentFormat = currentDataSources[selectedDataSource]?.format || 'qcow2';
                    switch (currentFormat) {
                      case 'qcow2':
                        return qcow2Logs;
                      case 'iso':
                        return isoLogs;
                      case 'vmdk':
                        return vmdkLogs;
                      default:
                        return [];
                    }
                  };
                  
                  const allLogs = getCurrentLogsForSource();
                  const selectedData = allLogs.map(log => log.text);
                  
                  // Update buffer based on itemCount
                  const currentBuffer = itemCount > selectedData.length 
                    ? selectedData 
                    : selectedData.slice(0, itemCount);
                  
                  // Use current buffer for rendering
                  const displayData = (!isPaused && currentBuffer.length > 0) 
                    ? currentBuffer.join('\n') 
                    : renderData;
                  
                  const displayItemCount = (!isPaused && currentBuffer.length > 0) 
                    ? currentBuffer.length 
                    : currentItemCount;
                  
                  const displayLinesBehind = (!isPaused && currentBuffer.length !== currentItemCount) 
                    ? currentBuffer.length - currentItemCount 
                    : linesBehind;
                  
                  // Filter logs based on search query only (no phase filter)
                  const filteredLogs = (() => {
                    if (!logSearchText) return allLogs;
                    
                    const { attributes, text } = parseSearchQuery(logSearchText);
                    return allLogs.filter(log => {
                      // Check attribute filters
                      if (attributes.stage) {
                        const stageMatch = attributes.stage === 'build' ? log.phase === 'BUILD' :
                                          attributes.stage === 'export' ? log.phase === 'EXPORT' :
                                          attributes.stage === 'system' ? log.phase === 'SYSTEM' : true;
                        if (!stageMatch) return false;
                      }
                      
                      if (attributes.status) {
                        let statusMatch = false;
                        if (attributes.status === 'error') {
                          statusMatch = log.isError;
                        } else if (attributes.status === 'success') {
                          statusMatch = log.isSuccess;
                        } else {
                          statusMatch = attributes.status === log.level.toLowerCase();
                        }
                        if (!statusMatch) return false;
                      }
                      
                      if (attributes.level) {
                        if (log.level.toLowerCase() !== attributes.level) return false;
                      }
                      
                      // Check text search
                      if (text) {
                        return log.text.toLowerCase().includes(text.toLowerCase());
                      }
                      
                      return true;
                    });
                  })();
                  
                  const handleExportLogs = () => {
                    const logs = getCurrentLogs();
                    const logContent = logs.map(log => log.text).join('\n');
                    const blob = new Blob([logContent], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedDataSource}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  };

                  const onExpandClick = (_event: React.MouseEvent) => {
                    const element = document.querySelector('#complex-toolbar-demo') as HTMLElement;
                    if (!element) return;

                    if (!isFullScreen) {
                      if (element.requestFullscreen) {
                        element.requestFullscreen();
                      } else if ((element as any).mozRequestFullScreen) {
                        (element as any).mozRequestFullScreen();
                      } else if ((element as any).webkitRequestFullScreen) {
                        (element as any).webkitRequestFullScreen((Element as any).ALLOW_KEYBOARD_INPUT);
                      }
                      setIsFullScreen(true);
                    } else {
                      if (document.exitFullscreen) {
                        document.exitFullscreen();
                      } else if ((document as any).webkitExitFullscreen) {
                        (document as any).webkitExitFullscreen();
                      } else if ((document as any).msExitFullscreen) {
                        (document as any).msExitFullscreen();
                      }
                      setIsFullScreen(false);
                    }
                  };

                  const onScroll = ({ scrollOffsetToBottom, scrollUpdateWasRequested }: any) => {
                    if (!scrollUpdateWasRequested) {
                      if (scrollOffsetToBottom > 0) {
                        setIsPaused(true);
                      } else {
                        setIsPaused(false);
                      }
                    }
                  };

                  // Create grouped menu options
                  // Group 1: Image build
                  // Note: buildOptionValue and exportDataSourcesForMenu are already defined above
                  const buildOptionLabel = selectedBuild.buildName || selectedBuild.name || `Build ${selectedBuild.id}`;
                  
                  // Update selectedDataSource if it doesn't match current selectedBuild's export file names
                  // This ensures the toggle displays correctly when selectedBuild changes
                  // Note: We handle this synchronously during render instead of useEffect
                  const currentBuildOptionValue = `build-${selectedBuild.id}`;
                  if (selectedDataSource !== currentBuildOptionValue && !exportDataSourcesForMenu[selectedDataSource]) {
                    // If selectedDataSource doesn't match, we'll default to build option
                    // This will be handled by the initial state logic
                  }
                  
                  // Get image name for Image build option
                  const buildImageName = selectedBuild.outputImage || selectedBuild.name || '';

                  const menuContent = (
                    <MenuContent>
                      <MenuGroup label="Image build" labelHeadingLevel="h3">
                        <MenuList>
                          <MenuItem
                            itemId={buildOptionValue}
                            isSelected={selectedDataSource === buildOptionValue}
                            description={buildImageName}
                          >
                            {buildOptionLabel}
                          </MenuItem>
                        </MenuList>
                      </MenuGroup>
                      <Divider />
                      <MenuGroup label="Export image" labelHeadingLevel="h3">
                        <MenuList>
                          {Object.entries(exportDataSourcesForMenu).map(([value, { label }]) => {
                            // The value is already the export file name (e.g., imagebuild-051982-qcow2-d26612)
                            const exportFileName = value;
                            return (
                              <MenuItem
                                key={value}
                                itemId={value}
                                isSelected={selectedDataSource === value}
                                description={exportFileName}
                              >
                                {label}
                              </MenuItem>
                            );
                          })}
                        </MenuList>
                      </MenuGroup>
                    </MenuContent>
                  );

                  // Update placeholder based on selection - show badge and name for all options
                  // This function is called directly in the toggle to ensure it's reactive
                  const getPlaceholder = (): React.ReactNode => {
                    // Check if Image Build is selected
                    if (selectedDataSource === buildOptionValue) {
                      return (
                        <React.Fragment>
                          <Badge>{buildOptionLabel}</Badge>
                          <span style={{ marginLeft: '8px' }}>{buildImageName}</span>
                        </React.Fragment>
                      );
                    }
                    
                    // For export options, get the label and file name from the data sources
                    const selectedExport = exportDataSourcesForMenu[selectedDataSource];
                    if (selectedExport) {
                      // The key (selectedDataSource) is the export file name
                      const exportFileName = selectedDataSource;
                      return (
                        <React.Fragment>
                          <Badge>{selectedExport.label}</Badge>
                          <span style={{ marginLeft: '8px' }}>{exportFileName}</span>
                        </React.Fragment>
                      );
                    }
                    
                    // Fallback: if selectedDataSource doesn't match, try to find by format
                    // or default to first available export or Image Build
                    const firstExport = Object.values(exportDataSourcesForMenu)[0];
                    const firstExportKey = Object.keys(exportDataSourcesForMenu)[0];
                    if (firstExport && firstExportKey) {
                      return (
                        <React.Fragment>
                          <Badge>{firstExport.label}</Badge>
                          <span style={{ marginLeft: '8px' }}>{firstExportKey}</span>
                        </React.Fragment>
                      );
                    }
                    
                    // Last resort: show Image Build
                    return (
                      <React.Fragment>
                        <Badge>{buildOptionLabel}</Badge>
                        <span style={{ marginLeft: '8px' }}>{buildImageName}</span>
                      </React.Fragment>
                    );
                  };

                  // Only show status badge for exports, not for Image Build
                  const showStatusBadge = selectedDataSource !== buildOptionValue;
                  
                  // Get the export status for the currently selected export format
                  // This should match the status shown in the image build table row export image statuses
                  const getCurrentExportStatus = (): ImageExport['status'] => {
                    // If Image Build is selected, return undefined (no status to show)
                    if (selectedDataSource === buildOptionValue) {
                      return 'pending';
                    }
                    
                    // CRITICAL: Get build status FIRST - check both selectedBuild and build
                    // This ensures Red Hat Edge Manager 1.1 shows complete status correctly
                    const buildStatus = selectedBuild ? selectedBuild.status : (build ? build.status : undefined);
                    
                    // ABSOLUTE CHECK: If build is complete, ALL exports MUST show complete
                    // This applies to Red Hat Edge Manager 1.1 navigation
                    // This check MUST happen FIRST before checking individual export status
                    if (buildStatus === 'complete') {
                      return 'complete';
                    }
                    
                    // Additional explicit check: If build ID is 'build-1' (the last complete row), force complete
                    if ((selectedBuild && selectedBuild.id === 'build-1') || (build && build.id === 'build-1')) {
                      return 'complete';
                    }
                    
                    // Now check individual export status
                    const exportDataSourcesForStatus = exportDataSourcesForMenu;
                    const currentFormat = exportDataSourcesForStatus[selectedDataSource]?.format || 'qcow2';
                    let currentExport: ImageExport | undefined;
                    if (currentFormat === 'qcow2') {
                      currentExport = qcow2Export;
                    } else if (currentFormat === 'iso') {
                      currentExport = isoExport;
                    } else if (currentFormat === 'vmdk') {
                      currentExport = vmdkExport;
                    }
                    
                    // If export status is complete, return complete
                    if (currentExport && currentExport.status === 'complete') {
                      return 'complete';
                    }
                    
                    // For other statuses, use the export's actual status
                    if (currentExport && currentExport.status) {
                      return currentExport.status;
                    }
                    
                    // Default based on build status
                    if (buildStatus === 'pushing' || buildStatus === 'building') {
                      return 'exporting';
                    }
                    
                    return 'pending';
                  };

                  // Get the current export status
                  const currentExportStatus = getCurrentExportStatus();

                  // Calculate width for dropdown to match longest option (including build name and image/export names)
                  const calculateDropdownWidth = (): number => {
                    // Get all possible option labels and their corresponding names
                    const buildName = buildOptionLabel;
                    const buildImageNameText = buildImageName;
                    const exportLabels = Object.values(exportDataSourcesForMenu).map(({ label }) => label);
                    const exportFileNames = Object.keys(exportDataSourcesForMenu);
                    
                    // Create a temporary element to measure text width
                    const tempElement = document.createElement('span');
                    tempElement.style.visibility = 'hidden';
                    tempElement.style.position = 'absolute';
                    tempElement.style.fontSize = '14px';
                    tempElement.style.fontFamily = 'Red Hat Text VF, sans-serif';
                    tempElement.style.padding = '8px 16px';
                    document.body.appendChild(tempElement);
                    
                    let maxWidth = 0;
                    
                    // Measure Image Build option: badge + name
                    tempElement.innerHTML = `<span>${buildName}</span> <span style="margin-left: 8px;">${buildImageNameText}</span>`;
                    const buildWidth = tempElement.offsetWidth;
                    if (buildWidth > maxWidth) maxWidth = buildWidth;
                    
                    // Measure each export option: badge + file name
                    exportLabels.forEach((label, index) => {
                      const exportFileName = exportFileNames[index];
                      tempElement.innerHTML = `<span>${label}</span> <span style="margin-left: 8px;">${exportFileName}</span>`;
                      const width = tempElement.offsetWidth;
                      if (width > maxWidth) maxWidth = width;
                    });
                    
                    document.body.removeChild(tempElement);
                    // Add padding for extra space (badge padding, icon, etc.)
                    return Math.max(maxWidth + 100, 400); // Minimum 400px, add 100px for padding and badge
                  };

                  const dropdownWidth = calculateDropdownWidth();

                  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
                    <MenuToggle 
                      ref={toggleRef} 
                      onClick={() => setSelectDataSourceOpen(!selectDataSourceOpen)}
                      isExpanded={selectDataSourceOpen}
                      style={{ minWidth: `${dropdownWidth}px` }}
                    >
                      {getPlaceholder()}
                    </MenuToggle>
                  );

                  const handleMenuSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, itemId: number | string | undefined) => {
                    const selection = itemId as string;
                    setSelectDataSourceOpen(false);
                    setSelectedDataSource(selection);
                    
                    // If Image Build is selected, don't set a format
                    // Note: buildOptionValue is already defined above
                    if (selection === buildOptionValue) {
                      setSelectedLogFormat('');
                              } else {
                                const exportDataSourcesForSelect = exportDataSourcesForMenu;
                                const format = exportDataSourcesForSelect[selection]?.format || 'qcow2';
                                setSelectedLogFormat(format);
                              }
                    
                    setLinesBehind(0);
                    setBuffer([]);
                    setItemCount(1);
                    setCurrentItemCount(0);
                  };

                  const leftAlignedToolbarGroup = (
                    <React.Fragment>
                      <ToolbarToggleGroup toggleIcon={<EllipsisVIcon />} breakpoint="md">
                        <ToolbarItem>
                          <Dropdown
                            toggle={toggle}
                            isOpen={selectDataSourceOpen}
                            onOpenChange={(isOpen) => setSelectDataSourceOpen(isOpen)}
                            popperProps={{
                              minWidth: `${dropdownWidth}px`
                            }}
                          >
                            <Menu
                              onSelect={handleMenuSelect}
                              activeItemId={selectedDataSource}
                              style={{ minWidth: `${dropdownWidth}px` }}
                            >
                              {menuContent}
                            </Menu>
                          </Dropdown>
                        </ToolbarItem>
                        <ToolbarItem>
                          <LogViewerSearch 
                            placeholder="Search"
                            value={logSearchText}
                            onChange={(value) => {
                              setLogSearchText(value);
                              setIsPaused(true);
                            }}
                            onClear={() => setLogSearchText('')}
                          />
                        </ToolbarItem>
                      </ToolbarToggleGroup>
                      {showStatusBadge && (
                        <ToolbarItem>
                          {getExportStatusBadge(currentExportStatus)}
                        </ToolbarItem>
                      )}
                    </React.Fragment>
                  );

                  const rightAlignedToolbarGroup = (
                    <React.Fragment>
                      <ToolbarGroup align={{ default: 'alignEnd' }}>
                        <ToolbarItem>
                          <Tooltip position="top" content={<div>Download</div>}>
                            <Button onClick={handleExportLogs} variant="plain" aria-label="Download current logs">
                              <DownloadIcon />
                            </Button>
                          </Tooltip>
                        </ToolbarItem>
                        <ToolbarItem>
                          <Tooltip position="top" content={<div>Expand</div>}>
                            <Button onClick={onExpandClick} variant="plain" aria-label="View log viewer in full screen">
                              <ExpandIcon />
                            </Button>
                          </Tooltip>
                        </ToolbarItem>
                      </ToolbarGroup>
                    </React.Fragment>
                  );


                  // Update state asynchronously to avoid render issues
                  React.useEffect(() => {
                    if (currentBuffer.length !== buffer.length) {
                      setBuffer(currentBuffer);
                    }
                    if (!isPaused && currentBuffer.length > 0) {
                      const newRenderData = currentBuffer.join('\n');
                      if (newRenderData !== renderData) {
                        setRenderData(newRenderData);
                        setCurrentItemCount(currentBuffer.length);
                        setTimeout(() => {
                          if (logViewerRef && logViewerRef.current) {
                            logViewerRef.current.scrollToBottom();
                          }
                        }, 0);
                      }
                    } else if (currentBuffer.length !== currentItemCount) {
                      setLinesBehind(currentBuffer.length - currentItemCount);
                    } else {
                      setLinesBehind(0);
                    }
                  }, [itemCount, selectedDataSource, isPaused, currentBuffer.length, buffer.length, renderData, currentItemCount]);

                  // Always use info variant for alerts
                  const alertVariant = 'info';

                  return (
                    <React.Fragment>
                      <Alert
                        variant={alertVariant}
                        isInline
                        className="app-info-alert"
                        title="Displaying the most recent 500 lines for optimal performance."
                      />
                      <LogViewer
                        data={displayData ? displayData.split('\n') : []}
                        id="complex-toolbar-demo"
                        scrollToRow={displayItemCount}
                        innerRef={logViewerRef}
                        height={isFullScreen ? '100%' : 600}
                        toolbar={
                          <Toolbar>
                            <ToolbarContent>
                              <ToolbarGroup align={{ default: 'alignStart' }}>{leftAlignedToolbarGroup}</ToolbarGroup>
                              <ToolbarGroup align={{ default: 'alignEnd' }}>{rightAlignedToolbarGroup}</ToolbarGroup>
                            </ToolbarContent>
                          </Toolbar>
                        }
                        overScanCount={10}
                        footer={undefined}
                        onScroll={onScroll}
                      />
                    </React.Fragment>
                  );
                })() : (
                <EmptyState>
                  <EmptyStateBody>
                      Build not found
                  </EmptyStateBody>
                </EmptyState>
                )}
              </PageSection>
            </TabContent>
          </Tab>
        </Tabs>
      </PageSection>
      
      {/* Download Started Modal - VMDK only */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showDownloadToast}
        onClose={() => setShowDownloadToast(false)}
      >
        <ModalHeader title="Download Started!" titleIconVariant="success" />
        <ModalBody>
          <div>
            While your 1GB image downloads, here's how to start:
            <div style={{ marginTop: '16px' }}>
              <div style={{ marginBottom: '8px' }}>• Import the file into your VM software.</div>
              <div style={{ marginBottom: '8px' }}>• Power on the VM.</div>
              <div>• Wait about 10 mins for the first-time setup to finish.</div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setShowDownloadToast(false)}>
            Got it
          </Button>
        </ModalFooter>
      </Modal>

      {/* Shortcuts Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
        aria-label="Keyboard shortcuts"
      >
        <ModalHeader>
          <Title headingLevel="h1" size="2xl">Shortcuts</Title>
        </ModalHeader>
        <ModalBody>
          <Stack hasGutter>
            <StackItem>
              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #d2d2d2',
                    borderRadius: '3px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    fontWeight: 500
                  }}>
                    F1
                  </div>
                </FlexItem>
                <FlexItem>View all editor shortcuts</FlexItem>
              </Flex>
            </StackItem>
            <StackItem>
              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      Ctrl
                    </div>
                    <span style={{ margin: '0 4px', color: '#6a6e73' }}>+</span>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      Space
                    </div>
                  </div>
                </FlexItem>
                <FlexItem>Activate auto complete</FlexItem>
              </Flex>
            </StackItem>
            <StackItem>
              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      Ctrl
                    </div>
                    <span style={{ margin: '0 4px', color: '#6a6e73' }}>+</span>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      Shift
                    </div>
                    <span style={{ margin: '0 4px', color: '#6a6e73' }}>+</span>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      M
                    </div>
                  </div>
                </FlexItem>
                <FlexItem>Toggle Tab action between insert Tab character and move focus out of editor</FlexItem>
              </Flex>
            </StackItem>
            <StackItem>
              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      Shift
                    </div>
                    <span style={{ margin: '0 4px', color: '#6a6e73' }}>+</span>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      ⌘
                    </div>
                    <span style={{ margin: '0 4px', color: '#6a6e73' }}>+</span>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      0
                    </div>
                  </div>
                </FlexItem>
                <FlexItem>View document outline</FlexItem>
              </Flex>
            </StackItem>
            <StackItem>
              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #d2d2d2',
                    borderRadius: '3px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    fontWeight: 500
                  }}>
                    Hover
                  </div>
                </FlexItem>
                <FlexItem>View property descriptions</FlexItem>
              </Flex>
            </StackItem>
            <StackItem>
              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      ⌘
                    </div>
                    <span style={{ margin: '0 4px', color: '#6a6e73' }}>+</span>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d2d2d2',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      S
                    </div>
                  </div>
                </FlexItem>
                <FlexItem>Save</FlexItem>
              </Flex>
            </StackItem>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setShowShortcutsModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Image Build Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      >
        <ModalHeader title="Delete this build?" />
        <ModalBody>
          <div>
            <div style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
              This will remove the record of this build and its history.
            </div>
            <div style={{ color: 'var(--pf-t--global--text--color--subtle)', fontSize: 'var(--pf-t--global--font--size--sm)' }}>
              The actual image files in your storage will not be deleted.
            </div>
          </div>
        </ModalBody>
          <ModalFooter>
            <Button
              key="delete"
              variant="danger"
              onClick={() => {
                // TODO: Implement delete logic
                console.log(`Deleting build: ${build.id}`);
                setShowDeleteModal(false);
                // Navigate back to image builds list after deletion
                navigate(getBasePath());
              }}
            >
              Delete
            </Button>
            <Button
              key="cancel"
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { ViewImageBuild };
